// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  LinkedLiquidityGate,
  LinkedLiquiditySectionApi,
} from '@client/banks/components/shared/linked-liquidity-section';
import { useTransactionForm } from '@client/banks/hooks/use-transaction-form';
import type { BankTransaction } from '@client/banks/types/transaction';
import {
  createEmptyBankTransaction,
  getDefaultBankCurrency,
} from '@client/banks/utils/bank-form-defaults';
import { commitInstrumentWithLinkedAccount } from '@client/banks/utils/commit-instrument-with-linked-account';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import { hasLinkableAccountIban } from '@client/banks/utils/account-reference';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';

export const useTransactionsForm = () => {
  const { navigate } = useStepsData();
  const {
    formData,
    listEditor,
    currencyOptions,
    accountIbanOptions,
    replaceFormData,
    closeListEditor,
  } = useBankUploadData();

  const linkedLiquidityApiRef = useRef<LinkedLiquiditySectionApi | null>(null);

  const [linkedLiquidityGate, setLinkedLiquidityGate] =
    useState<LinkedLiquidityGate>({
      isCreateMode: false,
      isAccountFormValid: true,
    });

  const transactions = useMemo(
    () => formData.transactions ?? [],
    [formData.transactions],
  );

  const defaultCurrency = useMemo(
    () => getDefaultBankCurrency(currencyOptions),
    [currencyOptions],
  );

  const defaultValues = useMemo(() => {
    const emptyTransaction = createEmptyBankTransaction(defaultCurrency);

    if (listEditor?.section !== 'transactions') {
      return emptyTransaction;
    }

    if (listEditor.index < transactions.length) {
      return { ...transactions[listEditor.index] };
    }

    return emptyTransaction;
  }, [defaultCurrency, listEditor, transactions]);

  const form = useTransactionForm({
    variant: 'create',
    defaultValues,
  });

  const iban = useWatch({ control: form.control, name: 'iban' });

  useEffect(() => {
    if (!hasLinkableAccountIban(iban)) {
      return;
    }

    const account = formData.accounts.find(
      accountItem => accountItem.iban === iban,
    );

    if (!account || account.currency === form.getValues('currency')) {
      return;
    }

    form.setValue('currency', account.currency, { shouldValidate: true });
  }, [form, formData.accounts, iban]);

  const isFormValid = useCallback(() => {
    if (!form.formState.isValid) {
      return false;
    }

    if (
      linkedLiquidityGate.isCreateMode &&
      !linkedLiquidityGate.isAccountFormValid
    ) {
      return false;
    }

    return true;
  }, [form.formState.isValid, linkedLiquidityGate]);

  const onSubmit = useCallback(
    async (data: BankTransaction) => {
      if (!listEditor || listEditor.section !== 'transactions') {
        return;
      }

      const linkedResult = await linkedLiquidityApiRef.current?.resolveSubmit();

      if (!linkedResult || linkedResult.mode === 'invalid') {
        return;
      }

      const nextFormData = commitInstrumentWithLinkedAccount(
        formData,
        'transactions',
        listEditor.index,
        {
          ...data,
          iban: linkedResult.mode === 'create' ? '' : linkedResult.iban,
        },
        linkedResult.mode === 'create' ? linkedResult.account : undefined,
      );

      replaceFormData(nextFormData);
      closeListEditor();
      navigate('transactions-list');
    },
    [closeListEditor, formData, listEditor, navigate, replaceFormData],
  );

  const onCancel = useCallback(() => {
    closeListEditor();
    navigate('transactions-list');
  }, [closeListEditor, navigate]);

  return {
    form,
    currencyOptions,
    accountIbanOptions,
    defaultCurrency,
    linkedLiquidityApiRef,
    onLinkedLiquidityGateChange: setLinkedLiquidityGate,
    isFormValid,
    onSubmit,
    onCancel,
  };
};
